import { CSSProperties, memo, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import * as echarts from "echarts";
import { MarkdownWrapper } from './Markdown.styled';
import './Markdown.css';

interface MarkdownProps {
    content: string;
    style?: CSSProperties;
}

const Markdown: React.FC<MarkdownProps> = (props) => {
    let components = useMemo(()=> {return {
        // 忽略 TS 类型警告（ReactMarkdown 组件类型声明较复杂，不影响功能）
        // @ts-ignore
        code({ node, inline, className, children, ...props }) {
            // 正则匹配代码语言：从 className 中提取 language-js / language-python 这类标识
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            // 判断：不是行内代码 + 成功匹配到语言 → 渲染「带语法高亮的代码块」
            if (!inline && match) {
                if (match[1] == 'echarts') {
                    return <EChartsRender code={children}></EChartsRender>
                } else {
                    return <SyntaxHighlighter
                        style={atomDark}
                        language={language}
                        PreTag="div"
                        customStyle={{
                            margin: 0,                // 去掉默认边距
                            borderRadius: '0.5rem',   // 圆角更美观
                            fontSize: '1.25rem',     // 代码字体大小
                            background: '#000000',    // 纯黑色背景
                        } as const}

                        // 透传剩余属性（保持原生功能）
                        {...props}
                    >
                        {/* 处理文本：去掉末尾多余换行，避免代码块底部空行 */}
                        {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                }
            } else {
                // 否则 → 渲染普通「行内代码」（如 `这里是行内代码`）
                <code className={className} {...props}>
                    {children}
                </code>
            }
        },
    }}, []);

    const splitCompleteAndPending = (text: string) => {
        const lastCloseIndex = text.lastIndexOf('```');

        // 如果找不到闭合标记，或者闭合标记后还有内容但未再次开启，需更复杂的逻辑
        // 这里简化为：如果 ``` 出现次数为偶数，说明所有代码块已闭合；奇数则有一个未闭合
        const matches = text.match(/```/g);
        const count = matches ? matches.length : 0;

        if (count % 2 === 0) {
            return { complete: text, pending: '' };
        } else {
            // 找到最后一个未闭合的 ``` 的位置
            const lastOpenIndex = text.lastIndexOf('```');
            // 完整部分是最后一个 ``` 之前的内容（包含上一个闭合的代码块）
            // 注意：这里需要确保 lastOpenIndex 对应的确实是开启标记，实际生产环境需更严谨的状态机
            const completePart = text.substring(0, lastOpenIndex);
            const pendingPart = text.substring(lastOpenIndex);
            return { complete: completePart, pending: pendingPart };
        }
    };

    let completeContent = splitCompleteAndPending(props.content).complete;
    return <MarkdownWrapper style={props.style} className="markdown-body">
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={components}
        >
            {completeContent}
        </ReactMarkdown>
    </MarkdownWrapper>
};

const EChartsRender: React.FC<{ code: any }> = memo(({ code }) => {
	const domRef = useRef<HTMLDivElement>();
	useEffect(() => {
		const initCharts = new Function('echartsDom', 'echarts', `${code};`);
		try {
			initCharts(domRef.current, echarts);
		} catch (e) {
			console.log(e);
		}
	}, [code]);

	return <div ref={domRef} style={{ width: '100%', height: '450px', }} className='echarts-container'></div>;
});
export default Markdown;